<template>
    <div class="w-full xl:w-6/12 flex flex-col gap-6 bg-white p-10 rounded-xl grow min-h-[500px]">
        <p class="text-md font-bold text-black">{{ $t('includes_market_symbols') }}</p>
        <div v-if="props.layout === 'list'" class="overflow-x-auto relative sm:rounded-lg h-[500px]">
            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                <thead class="text-xs text-gray-700 uppercase bg-gray-50/50 backdrop-blur-md dark:bg-gray-700 dark:text-gray-400 sticky top-0">
                    <tr>
                        <th scope="col" class="py-3 px-6 whitespace-nowrap">{{ $t('plain_name') }}</th>
                        <th scope="col" class="py-3 px-6 whitespace-nowrap">{{ $t('plain_price') }}</th>
                        <th scope="col" class="py-3 px-6 whitespace-nowrap">{{ $t('lang_currency') }}</th>
                        <th scope="col" class="py-3 px-6 whitespace-nowrap">Exchange</th>
                        <th scope="col" class="py-3 px-6 whitespace-nowrap">{{ $t('last_update') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <router-link
                        v-for="product in marketSymboleProduct"
                        :key="product.id"
                        v-slot="{ navigate }"
                        :to="product?.recent_contentstream ? lastMarketAnalysis(product.recent_contentstream) : '#'"
                        custom
                    >
                        <tr
                            :class="product?.recent_contentstream ? 'cursor-pointer' : ''"
                            class="bg-white border-b cursor-pointer hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                            @click="navigate"
                        >
                            <td class="py-4 px-6 flex flex-row items-center gap-2">
                                <img
                                    v-if="product.market_symbol?.logo"
                                    :src="product.market_symbol.logo"
                                    :alt="`${product.market_symbol.name} Logo`"
                                    class="w-8 h-8 rounded-full"
                                />
                                <div v-else class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">N/A</div>
                                <span>{{ product.market_symbol?.name ?? 'N/A' }}</span>
                            </td>

                            <td class="py-4 px-6">{{ product.market_symbol?.last_price ?? 'N/A' }}</td>
                            <td class="py-4 px-6">{{ product.market_symbol?.currency ?? '-' }}</td>
                            <td class="py-4 px-6">{{ product.market_symbol?.exchange ?? 'N/A' }}</td>
                            <td class="py-4 px-6">
                                {{ product.market_symbol?.last_price_time ? formatTimestamp(product.market_symbol.last_price_time) : 'N/A' }}
                            </td>
                        </tr>
                    </router-link>
                </tbody>
            </table>
        </div>
        <div v-else aria-label="tradingview-widget-container" class="h-[500px] rounded-xl"></div>
    </div>
</template>

<script setup>
import { defineProps, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { tradingViewConfig } from '../../../tradingViewWidget.config.js';
import { formatTimestamp } from '../../helpers.js';
import { lastMarketAnalysis } from '../priceTargets/utils.js';

const props = defineProps(['productId', 'layout', 'toggleTab']);
const store = useStore();
const technicalData = ref({});
const widgetPlaceholder = ref(null);
const script = ref(null);
const marketSymboleProduct = ref([]);

/**
 * Fetches technical data based on the provided product ID and layout.
 *
 * @return {Promise<void>}
 */
const fetchTechnicalData = async () => {
    if (!props.productId) {
        return;
    }

    if (props.layout === 'list') {
        marketSymboleProduct.value = await getMarketSymbolsProducts(props.productId);
    } else {
        store
            .dispatch('technical_data/show', {
                params: {
                    product_group_id: props.productId
                }
            })
            .then(() => {
                const getTechnicalData = store.getters['technical_data/get'];

                if (getTechnicalData.length > 0) {
                    technicalData.value = getTechnicalData[0];

                    initializeSingleChart();
                }
            });
    }
};

/**
 * Initializes the TradingView widget with the provided configuration.
 *
 * @param src
 * @param configuration
 */
const initializeTradingViewWidget = (src, configuration) => {
    widgetPlaceholder.value = document.querySelector('[aria-label="tradingview-widget-container"]');
    widgetPlaceholder.value.replaceChildren(); // empty placeholder
    script.value = document.createElement('script');
    script.value.src = src;
    script.value.async = true;
    script.value.innerHTML = JSON.stringify(configuration);
    widgetPlaceholder.value.appendChild(script.value);
};

/**
 * Initializes the TradingView widget for a single chart.
 *
 * @param tabNumber
 */
const initializeSingleChart = (tabNumber = 0) => {
    if (!technicalData.value.content || technicalData.value.content.length === 0) {
        return;
    }

    //
    const config = {
        ...tradingViewConfig,
        symbols: technicalData.value.content[tabNumber].fields.marketSymbol,
        theme: 'light'
    };

    initializeTradingViewWidget('https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js', config);
};

/**
 * Fetches market symbols based on the provided product ID.
 *
 * @param productID
 * @return {Promise<*>}
 */
const getMarketSymbolsProducts = async (productID) => {
    return store
        .dispatch('marketSymbols/product', {
            params: {
                product_id: productID
            }
        })
        .then(() => {
            const response = store.getters['marketSymbols/all'];
            marketSymboleProduct.value = response.data;

            return marketSymboleProduct.value;
        });
};

onMounted(() => {
    fetchTechnicalData();
});

watch(
    () => props.toggleTab,
    (tab) => {
        initializeSingleChart(+tab);
    }
);
</script>
